<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import BusinessDay from './BusinessDay.vue';
import {
  timeSlotParse,
  timeSlotTransform,
  defaultTimeSlot,
  timeZoneOptions,
} from '../helpers/businessHour';
import NextButton from 'dashboard/components-next/button/Button.vue';
// NOTICE: KAPTION-INTEGRATION - Response policy selector
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';

const DEFAULT_TIMEZONE = {
  label: 'Pacific Time (US & Canada) (GMT-07:00)',
  value: 'America/Los_Angeles',
};

export default {
  components: {
    SettingsSection,
    BusinessDay,
    NextButton,
    WootMessageEditor,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  // NOTICE: KAPTION-INTEGRATION - Response policy selector
  setup() {
    const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
    return { isKaptionFlagEnabled };
  },
  data() {
    return {
      isBusinessHoursEnabled: false,
      outsideHoursAction: 'ooo_only',
      unavailableMessage: '',
      timeZone: DEFAULT_TIMEZONE,
      // NOTICE: KAPTION-INTEGRATION - Mode-based response policies
      policyAlwaysId: null,
      policyBusinessHoursId: null,
      policyOutsideBusinessHoursId: null,
      dayNameKeys: {
        0: 'INBOX_MGMT.BUSINESS_HOURS.DAY.NAMES.SUNDAY',
        1: 'INBOX_MGMT.BUSINESS_HOURS.DAY.NAMES.MONDAY',
        2: 'INBOX_MGMT.BUSINESS_HOURS.DAY.NAMES.TUESDAY',
        3: 'INBOX_MGMT.BUSINESS_HOURS.DAY.NAMES.WEDNESDAY',
        4: 'INBOX_MGMT.BUSINESS_HOURS.DAY.NAMES.THURSDAY',
        5: 'INBOX_MGMT.BUSINESS_HOURS.DAY.NAMES.FRIDAY',
        6: 'INBOX_MGMT.BUSINESS_HOURS.DAY.NAMES.SATURDAY',
      },
      timeSlots: [...defaultTimeSlot],
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
      // NOTICE: KAPTION-INTEGRATION - Response policy selector
      responsePolicies: 'kaptionResponsePolicies/getPolicies',
    }),
    // NOTICE: KAPTION-INTEGRATION - Response policy selector
    showResponsePolicySelector() {
      return this.isKaptionFlagEnabled('KAPTION_RESPONSE_POLICIES');
    },
    policiesAlways() {
      return this.responsePolicies.filter(p => p.mode === 'always');
    },
    policiesBusinessHours() {
      return this.responsePolicies.filter(p => p.mode === 'business_hours');
    },
    policiesOutsideBusinessHours() {
      return this.responsePolicies.filter(
        p => p.mode === 'outside_business_hours'
      );
    },
    hasError() {
      if (!this.isBusinessHoursEnabled) return false;
      return this.timeSlots.filter(slot => slot.from && !slot.valid).length > 0;
    },
    timeZones() {
      return [...timeZoneOptions()];
    },
    showUnavailableMessage() {
      return this.outsideHoursAction !== 'assistant_only';
    },
    isRichEditorEnabled() {
      if (
        this.isATwilioChannel ||
        this.isATwitterInbox ||
        this.isAFacebookInbox
      )
        return false;
      return true;
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
    // NOTICE: KAPTION-INTEGRATION - Load response policies
    if (this.showResponsePolicySelector) {
      this.$store.dispatch('kaptionResponsePolicies/get');
    }
  },
  methods: {
    setDefaults() {
      const {
        working_hours_enabled: isEnabled = false,
        out_of_office_message: unavailableMessage,
        working_hours: timeSlots = [],
        timezone: timeZone,
        auto_assignment_config: autoConfig,
        // NOTICE: KAPTION-INTEGRATION - Mode-based response policies
        kaption_response_policy_always_id: alwaysId,
        kaption_response_policy_business_hours_id: bhId,
        kaption_response_policy_outside_business_hours_id: obhId,
      } = this.inbox;
      const slots = timeSlotParse(timeSlots).length
        ? timeSlotParse(timeSlots)
        : defaultTimeSlot;
      this.isBusinessHoursEnabled = isEnabled;
      this.outsideHoursAction =
        autoConfig?.kaption_assistant_handles_outside_hours
          ? 'assistant_only'
          : 'ooo_only';
      this.unavailableMessage = unavailableMessage || '';
      this.timeSlots = slots;
      this.timeZone =
        this.timeZones.find(item => timeZone === item.value) ||
        DEFAULT_TIMEZONE;
      // NOTICE: KAPTION-INTEGRATION - Mode-based response policies
      this.policyAlwaysId = alwaysId || null;
      this.policyBusinessHoursId = bhId || null;
      this.policyOutsideBusinessHoursId = obhId || null;
    },
    onSlotUpdate(slotIndex, slotData) {
      this.timeSlots = this.timeSlots.map(item =>
        item.day === slotIndex ? slotData : item
      );
    },
    async updateInbox() {
      try {
        const existingConfig = this.inbox.auto_assignment_config || {};
        const payload = {
          id: this.inbox.id,
          formData: false,
          working_hours_enabled: this.isBusinessHoursEnabled,
          out_of_office_message: this.unavailableMessage,
          working_hours: timeSlotTransform(this.timeSlots),
          timezone: this.timeZone.value,
          auto_assignment_config: {
            ...existingConfig,
            kaption_assistant_handles_outside_hours:
              this.outsideHoursAction === 'assistant_only',
          },
          // NOTICE: KAPTION-INTEGRATION - Mode-based response policies
          kaption_response_policy_always_id: this.policyAlwaysId || null,
          kaption_response_policy_business_hours_id:
            this.policyBusinessHoursId || null,
          kaption_response_policy_outside_business_hours_id:
            this.policyOutsideBusinessHoursId || null,
          channel: {},
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.BUSINESS_HOURS.TITLE')"
      :sub-title="$t('INBOX_MGMT.BUSINESS_HOURS.SUBTITLE')"
    >
      <form @submit.prevent="updateInbox">
        <label for="toggle-business-hours" class="toggle-input-wrap">
          <input
            v-model="isBusinessHoursEnabled"
            type="checkbox"
            class="ltr:mr-2 rtl:ml-2"
            name="toggle-business-hours"
          />
          {{ $t('INBOX_MGMT.BUSINESS_HOURS.TOGGLE_AVAILABILITY') }}
        </label>
        <p class="mb-4 text-n-slate-11">
          {{ $t('INBOX_MGMT.BUSINESS_HOURS.TOGGLE_HELP') }}
        </p>
        <div v-if="isBusinessHoursEnabled" class="mb-6">
          <div
            class="p-4 mb-4 rounded-lg border border-n-weak bg-n-alpha-black2"
          >
            <label class="mb-2 font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.BUSINESS_HOURS.OUTSIDE_HOURS_TITLE') }}
            </label>
            <label class="flex items-start gap-2 p-2 cursor-pointer">
              <input
                v-model="outsideHoursAction"
                type="radio"
                name="outside-hours-action"
                value="ooo_only"
                class="mt-1"
              />
              <span>
                <span class="font-medium text-n-slate-12">
                  {{ $t('INBOX_MGMT.BUSINESS_HOURS.OUTSIDE_HOURS_OOO_ONLY') }}
                </span>
                <span class="block text-sm text-n-slate-11">
                  {{
                    $t('INBOX_MGMT.BUSINESS_HOURS.OUTSIDE_HOURS_OOO_ONLY_HELP')
                  }}
                </span>
              </span>
            </label>
            <label class="flex items-start gap-2 p-2 cursor-pointer">
              <input
                v-model="outsideHoursAction"
                type="radio"
                name="outside-hours-action"
                value="assistant_only"
                class="mt-1"
              />
              <span>
                <span class="font-medium text-n-slate-12">
                  {{ $t('INBOX_MGMT.BUSINESS_HOURS.OUTSIDE_HOURS_ASSISTANT') }}
                </span>
                <span class="block text-sm text-n-slate-11">
                  {{
                    $t('INBOX_MGMT.BUSINESS_HOURS.OUTSIDE_HOURS_ASSISTANT_HELP')
                  }}
                </span>
              </span>
            </label>
          </div>
          <div v-if="showUnavailableMessage">
            <label class="unavailable-input-wrap">
              {{ $t('INBOX_MGMT.BUSINESS_HOURS.UNAVAILABLE_MESSAGE_LABEL') }}
            </label>
            <div
              v-if="isRichEditorEnabled"
              class="px-4 py-0 mx-0 mt-0 mb-4 rounded-lg outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 bg-n-alpha-black2"
            >
              <WootMessageEditor
                v-model="unavailableMessage"
                enable-variables
                is-format-mode
                :min-height="4"
              />
            </div>
            <textarea v-else v-model="unavailableMessage" type="text" />
          </div>
          <div class="timezone-input-wrap">
            <label>
              {{ $t('INBOX_MGMT.BUSINESS_HOURS.TIMEZONE_LABEL') }}
            </label>
            <multiselect
              v-model="timeZone"
              :options="timeZones"
              deselect-label=""
              select-label=""
              selected-label=""
              track-by="value"
              label="label"
              close-on-select
              :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
              :allow-empty="false"
            />
          </div>

          <label>
            {{ $t('INBOX_MGMT.BUSINESS_HOURS.WEEKLY_TITLE') }}
          </label>
          <BusinessDay
            v-for="timeSlot in timeSlots"
            :key="timeSlot.day"
            :day-name="$t(dayNameKeys[timeSlot.day])"
            :time-slot="timeSlot"
            @update="data => onSlotUpdate(timeSlot.day, data)"
          />
        </div>
        <!-- NOTICE: KAPTION-INTEGRATION - Mode-based Response Policy selectors -->
        <div
          v-if="showResponsePolicySelector"
          class="mt-6 pt-6 border-t border-n-weak"
        >
          <h4 class="mb-1 font-medium text-n-slate-12">
            {{ $t('KAPTION_RESPONSE_POLICIES.INBOX.POLICIES_TITLE') }}
          </h4>
          <p class="mb-4 text-sm text-n-slate-11">
            {{ $t('KAPTION_RESPONSE_POLICIES.INBOX.POLICIES_DESCRIPTION') }}
          </p>
          <div class="flex flex-col gap-3">
            <!-- Always (24/7) slot -->
            <div class="flex items-center gap-3">
              <span class="w-44 shrink-0 text-sm font-medium text-n-slate-12">
                {{ $t('KAPTION_RESPONSE_POLICIES.INBOX.SLOT_ALWAYS') }}
              </span>
              <select
                v-model="policyAlwaysId"
                class="w-full max-w-xs rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-b-solid focus:ring-1 focus:ring-b-solid"
              >
                <option :value="null">
                  {{ $t('KAPTION_RESPONSE_POLICIES.INBOX.NO_POLICY') }}
                </option>
                <option
                  v-for="policy in policiesAlways"
                  :key="policy.id"
                  :value="policy.id"
                >
                  {{ policy.name }}
                </option>
              </select>
            </div>
            <!-- Business Hours slot -->
            <div class="flex items-center gap-3">
              <span class="w-44 shrink-0 text-sm font-medium text-n-slate-12">
                {{ $t('KAPTION_RESPONSE_POLICIES.INBOX.SLOT_BH') }}
              </span>
              <select
                v-model="policyBusinessHoursId"
                class="w-full max-w-xs rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-b-solid focus:ring-1 focus:ring-b-solid"
              >
                <option :value="null">
                  {{ $t('KAPTION_RESPONSE_POLICIES.INBOX.NO_POLICY') }}
                </option>
                <option
                  v-for="policy in policiesBusinessHours"
                  :key="policy.id"
                  :value="policy.id"
                >
                  {{ policy.name }}
                </option>
              </select>
            </div>
            <!-- Outside Business Hours slot -->
            <div class="flex items-center gap-3">
              <span class="w-44 shrink-0 text-sm font-medium text-n-slate-12">
                {{ $t('KAPTION_RESPONSE_POLICIES.INBOX.SLOT_OBH') }}
              </span>
              <select
                v-model="policyOutsideBusinessHoursId"
                class="w-full max-w-xs rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-b-solid focus:ring-1 focus:ring-b-solid"
              >
                <option :value="null">
                  {{ $t('KAPTION_RESPONSE_POLICIES.INBOX.NO_POLICY') }}
                </option>
                <option
                  v-for="policy in policiesOutsideBusinessHours"
                  :key="policy.id"
                  :value="policy.id"
                >
                  {{ policy.name }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <NextButton
          type="submit"
          :label="$t('INBOX_MGMT.BUSINESS_HOURS.UPDATE')"
          :is-loading="uiFlags.isUpdating"
          :disabled="hasError"
          class="mt-4"
        />
      </form>
    </SettingsSection>
  </div>
</template>

<style lang="scss" scoped>
.timezone-input-wrap {
  &::v-deep .multiselect {
    @apply mt-2;
  }
}

::v-deep.message-editor {
  @apply border-0;
}

.unavailable-input-wrap {
  textarea {
    @apply min-h-[4rem] mt-2;
  }
}
</style>
